// src/components/Invitation/InvitePage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserContext } from "../../contexts/UserContext";
import axios from "axios";
import CollaboratorSignup from "./CollaboratorSignup";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../../firebase/config";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import './invitation.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://southamerica-east1-drecenna.cloudfunctions.net/api';

function InvitePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, setupAuthHeaders } = useUserContext();
  
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // New states for enhanced account handling
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Extract token from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    
    if (!token) {
      setError("Invalid invitation link. No token provided.");
      setIsVerifying(false);
      return;
    }
    
    // Verify the token using our API instead of direct Firestore access
    const verifyToken = async () => {
      try {
        setIsVerifying(true);
        
        console.log("Verifying invitation token:", token);
        
        // Call the API to verify the invitation
        const response = await axios.get(
          `${API_URL}/verify-invitation?token=${token}`
        );
        
        console.log("Invitation verification response:", response.data);
        
        if (response.data.success) {
          const invitation = response.data.invitation;
          const profile = response.data.profile;
          
          // Save debug info
          setDebugInfo({
            token,
            invitation,
            profile,
            verifiedAt: new Date().toISOString()
          });
          
          // Save invitation data for display
          setInvitationData({
            token,
            ownerUid: invitation.ownerUid,
            profileId: invitation.profileId,
            profileName: profile.profileName || "Business Profile",
            createdAt: new Date(invitation.createdAt),
            expiresAt: new Date(invitation.expiresAt)
          });
        } else {
          setError(response.data.error || "This invitation is no longer valid.");
          setDebugInfo({
            token,
            error: response.data.error,
            errorResponse: response.data
          });
        }
        
        setIsVerifying(false);
      } catch (error) {
        console.error("Error verifying invitation:", error);
        
        let errorMessage = "Could not verify the invitation. Please try again.";
        
        if (error.response) {
          // The request was made and the server responded with an error status
          errorMessage = error.response.data?.error || errorMessage;
          
          setDebugInfo({
            token,
            errorType: "API Error",
            status: error.response.status,
            errorData: error.response.data,
            timestamp: new Date().toISOString()
          });
        } else if (error.request) {
          // The request was made but no response was received
          errorMessage = "Server did not respond. Please check your connection.";
          
          setDebugInfo({
            token,
            errorType: "Network Error",
            errorMessage: "No response received from server",
            timestamp: new Date().toISOString()
          });
        } else {
          // Something happened in setting up the request
          setDebugInfo({
            token,
            errorType: "Request Setup Error",
            errorMessage: error.message,
            timestamp: new Date().toISOString()
          });
        }
        
        setError(errorMessage);
        setIsVerifying(false);
      }
    };
    
    verifyToken();
  }, [location.search]);

  // Handle accepting the invitation
  const handleAcceptInvitation = async () => {
    try {
      setIsAccepting(true);
      setError(null);
      
      if (!user) {
        // If not logged in, show account options
        setShowLogin(true);
        setIsAccepting(false);
        return;
      }
      
      console.log("User attempting to join workspace:", {
        userId: user.uid,
        invitation: invitationData
      });
      
      // Add to debug info
      setDebugInfo(prev => ({
        ...prev,
        acceptAttempt: {
          userId: user.uid,
          timestamp: new Date().toISOString()
        }
      }));
      
      // SOLUÇÃO: Implementar a lógica direto no cliente, sem depender da API
      // Isso evita os problemas com o endpoint /accept-invitation
      try {
        // 1. Verificar se o usuário já é um colaborador
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (!userDocSnap.exists()) {
          throw new Error("User document not found");
        }
        
        const userData = userDocSnap.data();
        const isAlreadyCollaborator = userData.isCollaborator === true;
        const existingWorkspaces = userData.collaboratorWorkspaces || [];
        
        // 2. Verificar se o convite ainda é válido
        const invitationRef = doc(db, 'invitations', invitationData.token);
        const invitationSnap = await getDoc(invitationRef);
        
        if (!invitationSnap.exists()) {
          throw new Error("Invitation not found");
        }
        
        const invitationDataFromDB = invitationSnap.data();
        
        if (invitationDataFromDB.status !== 'pending') {
          throw new Error("This invitation has already been used or revoked");
        }
        
        const expiresAt = invitationDataFromDB.expiresAt.toDate();
        if (new Date() > expiresAt) {
          // Marcar como expirado
          await updateDoc(invitationRef, {
            status: 'expired',
            updatedAt: new Date()
          });
          throw new Error("This invitation has expired");
        }
        
        // 3. Verificar se o perfil existe
        const profileRef = doc(
          db, 
          'users', 
          invitationDataFromDB.ownerUid, 
          'businessProfiles', 
          invitationDataFromDB.profileId
        );
        
        const profileSnap = await getDoc(profileRef);
        
        if (!profileSnap.exists()) {
          throw new Error("The business profile no longer exists");
        }
        
        const profileData = profileSnap.data();
        
        // 4. Verificar se o usuário já é membro
        const members = profileData.members || {};
        
        if (members[user.uid]) {
          // Se o usuário já for membro, simplesmente redirecionar para o dashboard
          console.log("User is already a member of this workspace");
          setSuccess(true);
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
          return;
        }
        
        // 5. Criar o objeto de workspace
        const newWorkspace = {
          ownerUid: invitationDataFromDB.ownerUid,
          profileId: invitationDataFromDB.profileId,
          workspaceName: profileData.profileName || "Workspace",
          invitedAt: new Date().toISOString(),
          role: 'collaborator',
          acceptedAt: new Date().toISOString()
        };
        
        // 6. Executar as atualizações
        
        // 6.1. Adicionar o usuário como membro do perfil
        members[user.uid] = 'collaborator';
        
        await updateDoc(profileRef, {
          members: members,
          updatedAt: new Date()
        });
        
        // 6.2. Atualizar o documento do usuário
        if (isAlreadyCollaborator) {
          // Verificar se o workspace já existe
          const workspaceExists = existingWorkspaces.some(
            w => w.ownerUid === newWorkspace.ownerUid && 
                 w.profileId === newWorkspace.profileId
          );
          
          if (!workspaceExists) {
            console.log("Adding new workspace to existing collaborator account");
            await updateDoc(userDocRef, {
              collaboratorWorkspaces: [...existingWorkspaces, newWorkspace],
              updatedAt: new Date()
            });
          } else {
            console.log("Workspace already exists in collaborator's workspaces");
            // Atualizar o workspace existente
            const updatedWorkspaces = existingWorkspaces.map(w => 
              (w.ownerUid === newWorkspace.ownerUid && 
               w.profileId === newWorkspace.profileId) 
                ? { ...w, ...newWorkspace } 
                : w
            );
            
            await updateDoc(userDocRef, {
              collaboratorWorkspaces: updatedWorkspaces,
              updatedAt: new Date()
            });
          }
        } else {
          // Primeiro, atualizar campos básicos
          await updateDoc(userDocRef, {
            isCollaborator: true,
            collaboratorSince: new Date(),
            invitedBy: invitationDataFromDB.ownerUid,
            originalInvitation: {
              token: invitationData.token,
              profileId: invitationDataFromDB.profileId,
              workspaceName: profileData.profileName || "Workspace"
            },
            updatedAt: new Date()
          });
          
          // Depois, adicionar o array de workspaces separadamente
          await updateDoc(userDocRef, {
            collaboratorWorkspaces: [newWorkspace]
          });
        }
        
        // 6.3. Marcar o convite como aceito
        await updateDoc(invitationRef, {
          status: 'accepted',
          acceptedBy: user.uid,
          acceptedAt: new Date()
        });
        
        console.log("Invitation accepted successfully via client-side solution");
        
        // Success!
        setSuccess(true);
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } catch (clientError) {
        console.error("Error in client-side invitation acceptance:", clientError);
        setError(clientError.message || "Failed to accept invitation. Please try again.");
      }
    } catch (error) {
      console.error("Error accepting invitation:", error);
      
      // Add to debug info
      setDebugInfo(prev => ({
        ...prev,
        generalError: {
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        }
      }));
      
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setIsAccepting(false);
    }
  };
  
  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (!loginEmail || !loginPassword) {
      setLoginError('Please enter both email and password');
      return;
    }
    
    try {
      setIsLoggingIn(true);
      
      // Sign in with Firebase Auth
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      
      // After successful login, close the login form and try to accept the invitation
      setShowLogin(false);
      // Wait a bit for auth state to update
      setTimeout(() => {
        handleAcceptInvitation();
      }, 500);
    } catch (error) {
      console.error("Login error:", error);
      
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        setLoginError('Invalid email or password');
      } else if (error.code === 'auth/too-many-requests') {
        setLoginError('Too many failed login attempts. Please try again later.');
      } else {
        setLoginError('Failed to log in: ' + error.message);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  // Handle successful signup
  const handleSignupSuccess = (newUser) => {
    console.log("Signup successful, new user:", newUser);
    setShowSignup(false);
    // Wait a bit for auth state to update
    setTimeout(() => {
      handleAcceptInvitation();
    }, 500);
  };

  // Display loading state while verifying token or waiting for user auth
  if (loading || isVerifying) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <h1 className="invitation-title">Verifying Invitation</h1>
          <div className="invitation-loading">
            <div className="invitation-spinner"></div>
          </div>
          <p className="invitation-text">Please wait while we verify your invitation...</p>
        </div>
      </div>
    );
  }

  // Display error message
  if (error) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <div className="invitation-status">
            <svg className="invitation-status-icon error" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <h1 className="invitation-title">Invalid Invitation</h1>
            <p className="invitation-text">{error}</p>
          </div>
          
          <button
            onClick={() => navigate("/")}
            className="invitation-action-button"
          >
            Go to Homepage
          </button>
          
          {/* Show debug info only in development */}
          {process.env.NODE_ENV === 'development' && debugInfo && (
            <div className="invitation-debug">
              <details>
                <summary className="invitation-debug-toggle">Debug Information</summary>
                <pre className="invitation-debug-content">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Display success message
  if (success) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <div className="invitation-status">
            <svg className="invitation-status-icon success" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h1 className="invitation-title">Success!</h1>
            <p className="invitation-text">
              You have successfully joined the workspace. Redirecting to dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Show login form
  if (showLogin) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <h2 className="invitation-title">Access Workspace</h2>
          
          <div className="mb-6">
            <p className="invitation-text">
              To join <span className="invitation-highlight">{invitationData.profileName}</span>, please log in or create an account:
            </p>
          </div>
          
          {!showSignup ? (
            <>
              <form onSubmit={handleLogin} className="invitation-form">
                <div className="invitation-form-group">
                  <label htmlFor="email" className="invitation-form-label">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="invitation-form-input"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
                
                <div className="invitation-form-group">
                  <label htmlFor="password" className="invitation-form-label">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="invitation-form-input"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                </div>
                
                {loginError && (
                  <div className="invitation-error">
                    {loginError}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="invitation-action-button"
                >
                  {isLoggingIn ? 'Logging in...' : 'Log In'}
                </button>
              </form>
              
              <div className="invitation-auth-toggle">
                <p className="invitation-auth-toggle-text">
                  Don't have an account yet?
                </p>
                <button
                  onClick={() => setShowSignup(true)}
                  className="invitation-auth-toggle-button"
                >
                  Create Free Collaborator Account
                </button>
              </div>
              
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowLogin(false)}
                  className="invitation-text-button"
                >
                  Back to Invitation
                </button>
              </div>
            </>
          ) : (
            <CollaboratorSignup 
              invitationData={invitationData}
              onSignupSuccess={handleSignupSuccess}
              onCancel={() => setShowSignup(false)}
            />
          )}
        </div>
      </div>
    );
  }

  // Display invitation details and accept button
  return (
    <div className="invitation-container">
      <div className="invitation-card">
        <h1 className="invitation-title">Workspace Invitation</h1>
        {invitationData && (
          <div>
            <p className="invitation-text">
              You have been invited to join:
            </p>
            <div className="invitation-info-box">
              <h2 className="invitation-subtitle">{invitationData.profileName}</h2>
              <p className="invitation-expires">
                This invitation expires on {invitationData.expiresAt.toLocaleString()}
              </p>
            </div>
            
            {!user && (
              <p className="invitation-note">
                You'll need to log in or create a free collaborator account to join this workspace.
              </p>
            )}
            
            <button
              onClick={handleAcceptInvitation}
              disabled={isAccepting}
              className="invitation-action-button"
            >
              {isAccepting ? "Joining..." : user ? "Join Workspace" : "Continue to Access"}
            </button>
          </div>
        )}
        
        {/* Show debug info only in development */}
        {process.env.NODE_ENV === 'development' && debugInfo && (
          <div className="invitation-debug">
            <details>
              <summary className="invitation-debug-toggle">Debug Information</summary>
              <pre className="invitation-debug-content">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvitePage;